"use client";

import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
	type CSSProperties,
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../lib";
import {
	buildScrollFadeMaskLayersCss,
	scrollFadeMaskStylesFromLayers,
} from "../custom/scroll-fade-edges";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
	ContentTooltipGroup,
} from "../design-system/tooltip";
import { OctagonBadge } from "./octagon-badge";
import type { SkillBadgeVariant } from "./octagon-badge-layer-tokens";
import { resolveSkillBadgeVisualFootprintPx } from "./octagon-geometry";
import { SkillBadgeShine } from "./skill-badge-shine";
import { SkillLucideIcon } from "./skill-lucide-icon";

/** Default skill badge size on entity structure stop cards. */
export const SKILL_BADGE_DEFAULT_SIZE_PX = 48;

/** Default icon size inside {@link SKILL_BADGE_DEFAULT_SIZE_PX}. */
export const SKILL_BADGE_DEFAULT_ICON_PX = 20;

/** Compact skill badge size for entity structure editor headers. */
export const SKILL_BADGE_ENTITY_PAGE_SIZE_PX = 40;

/** Icon size for {@link SKILL_BADGE_ENTITY_PAGE_SIZE_PX}. */
export const SKILL_BADGE_ENTITY_PAGE_ICON_PX = 18;

/**
 * SoftBadge row target height (`h-5` / 20px). Structure-header skill stacks
 * render at {@link SKILL_BADGE_ENTITY_PAGE_SIZE_PX} then CSS-scale to this.
 */
export const SKILL_BADGE_STRUCTURE_HEADER_TARGET_HEIGHT_PX = 20;

/** Visual gap between skill badge face and its tooltip (px). */
export const SKILL_BADGE_TOOLTIP_VISUAL_GAP_PX = -6;

/** Radix side offset when `layoutFootprint="visual"` (trigger matches face size). */
export const SKILL_BADGE_TOOLTIP_SIDE_OFFSET_PX =
	SKILL_BADGE_TOOLTIP_VISUAL_GAP_PX;

export interface SkillBadgeEntry {
	name: string;
	icon?: string | null;
}

export type { SkillBadgeVariant } from "./octagon-badge-layer-tokens";

/** `fixed` — bounded list; `infinite` — wraps from last to first and vice versa. */
export type SkillBadgesScrollBehavior = "fixed" | "infinite";

/** `carousel` — chevron pager; `group` — overlapping ContentTooltip morph stack. */
export type SkillBadgesLayout = "carousel" | "group";

/** Default autoplay interval when {@link SkillBadgesProps.autoplay} is enabled. */
export const SKILL_BADGE_AUTOPLAY_INTERVAL_MS = 3000;

export interface SkillBadgesProps {
	skills: SkillBadgeEntry[];
	scrollBehavior?: SkillBadgesScrollBehavior;
	/** Default `carousel`. Use `group` for compact overlapping stack (entity headers). */
	layout?: SkillBadgesLayout;
	size?: number;
	iconSize?: number;
	className?: string;
	tooltipSide?: "top" | "right" | "bottom" | "left";
	tooltipSideOffset?: number;
	/** Subtle host drop-shadow — lighter than octagon button default. */
	dropShadow?: boolean | "subtle";
	/** One-shot shine sweep on hover in; reverses on hover out. */
	hoverShine?: boolean;
	/** Chevron colour treatment — `on-dark` for navy reward panels. */
	navTone?: "default" | "on-dark";
	/** `earned` — gold reward chrome; `placeholder` — grey locked / unearned. */
	variant?: SkillBadgeVariant;
	/** Horizontal mask fade band length (px) on multi-skill carousel edges. */
	fadeEdgesThicknessPx?: number;
	/** Cycle skills automatically after {@link autoplayIntervalMs}. Default `false`. */
	autoplay?: boolean;
	/** Delay between autoplay advances (ms). Default {@link SKILL_BADGE_AUTOPLAY_INTERVAL_MS}. */
	autoplayIntervalMs?: number;
	/**
	 * Group layout only: render at {@link size} then CSS-scale so the visual
	 * footprint height equals this (keeps border/icon proportions). SoftBadge
	 * row uses {@link SKILL_BADGE_STRUCTURE_HEADER_TARGET_HEIGHT_PX}.
	 */
	groupScaleToHeightPx?: number;
}

interface SkillBadgeTileProps {
	skill: SkillBadgeEntry;
	size: number;
	iconSize: number;
	icon?: ReactNode;
	className?: string;
	tooltipSide: "top" | "right" | "bottom" | "left";
	tooltipSideOffset: number;
	dropShadow: boolean | "subtle" | false;
	hoverShine: boolean;
	shellTone: "system" | "light";
	variant: SkillBadgeVariant;
	/** When false, render face only (group stack owns tooltips). Default true. */
	withTooltip?: boolean;
	/**
	 * `button` — interactive host (carousel). `span` — non-interactive face
	 * safe inside parent `<button>` expand toggles (structure headers).
	 */
	faceAs?: "button" | "span";
}

function SkillBadgeTile({
	skill,
	size,
	iconSize,
	icon,
	className,
	tooltipSide,
	tooltipSideOffset,
	dropShadow,
	hoverShine,
	shellTone,
	variant,
	withTooltip = true,
	faceAs = "button",
}: SkillBadgeTileProps) {
	const [shineDirection, setShineDirection] = useState<"forward" | "reverse">(
		"reverse",
	);

	const handleShineEnter = useCallback(() => {
		if (!hoverShine) {
			return;
		}
		setShineDirection("forward");
	}, [hoverShine]);

	const handleShineLeave = useCallback(() => {
		if (!hoverShine) {
			return;
		}
		setShineDirection("reverse");
	}, [hoverShine]);

	const resolvedDropShadow =
		dropShadow === true
			? "subtle"
			: dropShadow === false
				? false
				: dropShadow;

	const showShine = hoverShine && variant !== "placeholder";
	const isPlaceholder = variant === "placeholder";

	const badge = (
		<OctagonBadge
			surface="skill"
			size={size}
			layoutFootprint="visual"
			dropShadow={resolvedDropShadow}
			shellTone={shellTone}
			skillVariant={variant}
			innerOverlay={
				showShine ? (
					<SkillBadgeShine direction={shineDirection} />
				) : null
			}
			ariaHidden
		>
			{icon ?? (
				<SkillLucideIcon
					icon={skill.icon}
					size={iconSize}
					variant={variant}
				/>
			)}
		</OctagonBadge>
	);

	const faceClassName = cn(
		"relative inline-flex shrink-0 cursor-default items-center justify-center overflow-visible border-0 bg-transparent p-0",
		isPlaceholder && "opacity-60",
		className,
	);

	const face =
		faceAs === "span" ? (
			<span
				className={faceClassName}
				role="img"
				aria-label={skill.name}
				onPointerEnter={showShine ? handleShineEnter : undefined}
				onPointerLeave={showShine ? handleShineLeave : undefined}
			>
				{badge}
			</span>
		) : (
			<button
				type="button"
				className={faceClassName}
				aria-label={skill.name}
				onPointerEnter={showShine ? handleShineEnter : undefined}
				onPointerLeave={showShine ? handleShineLeave : undefined}
			>
				{badge}
			</button>
		);

	if (!withTooltip) {
		return face;
	}

	return (
		<ContentTooltip
			{...CONTENT_TOOLTIP_SHORT}
			side={tooltipSide}
			sideOffset={tooltipSideOffset}
			surface="inverse"
			transition="morph"
			interactive
			dismissOnTriggerLeave={false}
			followCursor={false}
			openOn="hoverAndTap"
			content={skill.name}
			triggerClassName="inline-flex"
		>
			{face}
		</ContentTooltip>
	);
}

/** Grid tile hover scale motion — mirrors hexagon-map `hexagon-tile-surfaces.css`. */
const SKILL_BADGE_NAV_CHEVRON_HOVER_MOTION_CLASS =
	"transition-[color,transform] duration-[250ms] ease-[cubic-bezier(0.4,2,0.35,1)] motion-reduce:transition-none hover:scale-[1.2]";

const SKILL_BADGE_CAROUSEL_SLIDE_TRANSITION = {
	duration: 0.25,
	ease: [0.22, 1, 0.36, 1] as const,
};

const SKILL_BADGE_CAROUSEL_SLIDE_VARIANTS = {
	enter: ({
		direction,
		slideOffsetPx,
	}: {
		direction: number;
		slideOffsetPx: number;
	}) => ({
		x: direction >= 0 ? slideOffsetPx : -slideOffsetPx,
	}),
	center: {
		x: 0,
	},
	exit: ({
		direction,
		slideOffsetPx,
	}: {
		direction: number;
		slideOffsetPx: number;
	}) => ({
		x: direction >= 0 ? -slideOffsetPx : slideOffsetPx,
	}),
};

function resolveSkillBadgeCarouselFadeThicknessPx(size: number): number {
	return Math.max(6, Math.min(10, Math.round(size * 0.18)));
}

const SKILL_BADGE_CAROUSEL_MASK_VISIBILITY = {
	showLeft: true,
	showRight: true,
	showTop: false,
	showBottom: false,
} as const;

function skillBadgeCarouselMaskStyle(
	fadeEdgesThicknessPx: number,
): CSSProperties | undefined {
	const maskLayersCss = buildScrollFadeMaskLayersCss({
		axes: "horizontal",
		thicknessPx: fadeEdgesThicknessPx,
		topInsetPx: 0,
		vis: SKILL_BADGE_CAROUSEL_MASK_VISIBILITY,
	});
	const maskStyles = scrollFadeMaskStylesFromLayers(maskLayersCss);
	if (!maskStyles?.maskImage) {
		return undefined;
	}
	return {
		...maskStyles,
		WebkitMaskImage: maskStyles.maskImage,
	};
}

interface SkillBadgeCarouselViewportProps extends SkillBadgeTileProps {
	skillIndex: number;
	slideDirection: number;
	fadeEdgesThicknessPx: number;
}

function SkillBadgeCarouselViewport({
	skill,
	skillIndex,
	slideDirection,
	size,
	fadeEdgesThicknessPx,
	...tileProps
}: SkillBadgeCarouselViewportProps) {
	const reducedMotion = useReducedMotion();
	const slideTransition = reducedMotion
		? { duration: 0 }
		: SKILL_BADGE_CAROUSEL_SLIDE_TRANSITION;
	const gutterPx = fadeEdgesThicknessPx;
	const visualFootprintPx = resolveSkillBadgeVisualFootprintPx(size);
	const clipWidth = visualFootprintPx + gutterPx * 2;
	const slideOffsetPx = visualFootprintPx + gutterPx;
	const maskStyle = useMemo(
		() => skillBadgeCarouselMaskStyle(gutterPx),
		[gutterPx],
	);

	return (
		<div
			className="relative z-0 shrink-0 overflow-visible"
			style={{ width: visualFootprintPx, height: visualFootprintPx }}
		>
			<div
				className="absolute inset-y-0 overflow-hidden"
				style={{
					left: -gutterPx,
					width: clipWidth,
					...maskStyle,
				}}
			>
				<AnimatePresence initial={false} custom={slideDirection}>
					<motion.div
						key={`${skillIndex}:${skill.name}`}
						custom={{ direction: slideDirection, slideOffsetPx }}
						variants={SKILL_BADGE_CAROUSEL_SLIDE_VARIANTS}
						initial="enter"
						animate="center"
						exit="exit"
						transition={slideTransition}
						className="absolute inset-y-0 flex items-center justify-center will-change-transform"
						style={{ left: gutterPx, width: visualFootprintPx }}
					>
						<SkillBadgeTile
							skill={skill}
							size={size}
							{...tileProps}
						/>
					</motion.div>
				</AnimatePresence>
			</div>
		</div>
	);
}

/** Extra tap padding around the icon; negative margin keeps layout position unchanged. */
const SKILL_BADGE_NAV_HIT_SLOP_CLASS = "p-2 -m-2";

interface SkillBadgeNavChevronProps {
	direction: "prev" | "next";
	disabled: boolean;
	label: string;
	compact?: boolean;
	navTone?: "default" | "on-dark";
	onActivate: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent) => void;
	className?: string;
}

function SkillBadgeNavChevron({
	direction,
	disabled,
	label,
	compact = false,
	navTone = "default",
	onActivate,
	className,
}: SkillBadgeNavChevronProps) {
	const Icon = direction === "prev" ? ChevronLeftIcon : ChevronRightIcon;

	return (
		<button
			type="button"
			disabled={disabled}
			aria-label={label}
			onClick={(event) => {
				event.stopPropagation();
				onActivate(event);
			}}
			onPointerDown={(event) => event.stopPropagation()}
			className={cn(
				"relative inline-flex shrink-0 origin-center items-center justify-center rounded-sm",
				SKILL_BADGE_NAV_HIT_SLOP_CLASS,
				disabled
					? navTone === "on-dark"
						? "cursor-not-allowed text-white/30"
						: "cursor-not-allowed text-muted-foreground/35"
					: cn(
							"group cursor-pointer",
							SKILL_BADGE_NAV_CHEVRON_HOVER_MOTION_CLASS,
							navTone === "on-dark"
								? "text-white/55 hover:text-white"
								: "text-muted-foreground hover:text-foreground",
						),
				className,
			)}
		>
			<Icon
				className={cn(
					"shrink-0",
					compact ? "size-3.5" : "size-4 @[320px]:size-[18px]",
					disabled
						? "[stroke-width:2]"
						: "[stroke-width:2.5] transition-[stroke-width] duration-[250ms] ease-[cubic-bezier(0.4,2,0.35,1)] motion-reduce:transition-none group-hover:[stroke-width:4]",
				)}
				aria-hidden
			/>
		</button>
	);
}

/**
 * Octagonal skill badge(s) — grey face with amber gradient border and skill tooltip.
 * `carousel` (default): prev/next chevrons. `group`: overlapping ContentTooltip morph stack.
 */
export function SkillBadges({
	skills,
	scrollBehavior = "fixed",
	layout = "carousel",
	size = SKILL_BADGE_DEFAULT_SIZE_PX,
	iconSize = SKILL_BADGE_DEFAULT_ICON_PX,
	className,
	tooltipSide = "top",
	tooltipSideOffset = SKILL_BADGE_TOOLTIP_SIDE_OFFSET_PX,
	dropShadow = "subtle",
	hoverShine = false,
	navTone = "default",
	variant = "earned",
	fadeEdgesThicknessPx,
	autoplay = false,
	autoplayIntervalMs = SKILL_BADGE_AUTOPLAY_INTERVAL_MS,
	groupScaleToHeightPx,
}: SkillBadgesProps) {
	const reducedMotion = useReducedMotion();
	const resolvedSkills = useMemo(
		() => skills.filter((skill) => skill.name.trim().length > 0),
		[skills],
	);
	const [index, setIndex] = useState(0);
	const [slideDirection, setSlideDirection] = useState(0);
	const [isPointerOver, setIsPointerOver] = useState(false);
	const indexRef = useRef(index);
	indexRef.current = index;
	const count = resolvedSkills.length;
	const shouldAutoplay =
		layout === "carousel" && autoplay && count > 1 && !reducedMotion;
	const visualFootprintPx = resolveSkillBadgeVisualFootprintPx(size);

	useEffect(() => {
		if (index >= count) {
			setIndex(0);
			setSlideDirection(0);
		}
	}, [index, count]);

	const goPrev = useCallback(() => {
		setSlideDirection(-1);
		setIndex((current) => {
			if (scrollBehavior === "infinite") {
				return (current - 1 + count) % count;
			}
			return Math.max(0, current - 1);
		});
	}, [count, scrollBehavior]);

	const goNext = useCallback(() => {
		setSlideDirection(1);
		setIndex((current) => {
			if (scrollBehavior === "infinite") {
				return (current + 1) % count;
			}
			return Math.min(count - 1, current + 1);
		});
	}, [count, scrollBehavior]);

	const advanceAutoplay = useCallback(() => {
		const current = indexRef.current;
		if (scrollBehavior === "infinite") {
			setSlideDirection(1);
			setIndex((current + 1) % count);
			return;
		}
		if (current >= count - 1) {
			setSlideDirection(-1);
			setIndex(0);
			return;
		}
		setSlideDirection(1);
		setIndex(current + 1);
	}, [count, scrollBehavior]);

	useEffect(() => {
		if (!shouldAutoplay || isPointerOver) {
			return;
		}
		const intervalId = window.setInterval(() => {
			advanceAutoplay();
		}, autoplayIntervalMs);
		return () => {
			window.clearInterval(intervalId);
		};
	}, [advanceAutoplay, autoplayIntervalMs, isPointerOver, shouldAutoplay]);

	const handlePointerEnter = useCallback(() => {
		setIsPointerOver(true);
	}, []);

	const handlePointerLeave = useCallback(() => {
		setIsPointerOver(false);
	}, []);

	const shellTone = navTone === "on-dark" ? "light" : "system";

	const tileProps = {
		size,
		iconSize,
		tooltipSide,
		tooltipSideOffset,
		dropShadow,
		hoverShine,
		shellTone,
		variant,
	} as const;

	if (count === 0) {
		return null;
	}

	if (layout === "group") {
		const nativeFootprintPx = visualFootprintPx;
		const targetHeightPx =
			groupScaleToHeightPx != null && groupScaleToHeightPx > 0
				? groupScaleToHeightPx
				: nativeFootprintPx;
		const scale =
			nativeFootprintPx > 0 ? targetHeightPx / nativeFootprintPx : 1;
		const slotPx = nativeFootprintPx * scale;
		const overlapClass =
			slotPx <= SKILL_BADGE_STRUCTURE_HEADER_TARGET_HEIGHT_PX + 2
				? "-space-x-1.5"
				: slotPx <= SKILL_BADGE_ENTITY_PAGE_SIZE_PX
					? "-space-x-2.5"
					: "-space-x-3";
		const groupSideOffset =
			tooltipSideOffset < 0
				? Math.max(8, Math.round(slotPx * 0.45))
				: tooltipSideOffset;
		const morphGroupId = `skill-badges-group:${resolvedSkills
			.map((skill) => skill.name)
			.join("|")}`;

		return (
			<ContentTooltipGroup groupId={morphGroupId}>
				<div
					className={cn(
						"flex h-5 items-center self-center leading-none",
						overlapClass,
						className,
					)}
					style={{ height: targetHeightPx }}
					data-slot="skill-badges-group"
					onPointerDown={(event) => event.stopPropagation()}
				>
					{resolvedSkills.map((skill, skillIndex) => (
						<ContentTooltip
							key={`${skill.name}:${skillIndex}`}
							groupId={morphGroupId}
							side={tooltipSide}
							sideOffset={groupSideOffset}
							surface="inverse"
							colorScheme="system"
							size="sm"
							transition={reducedMotion ? "fade" : "morph"}
							followCursor={false}
							interactive
							dismissOnTriggerLeave={false}
							openOn="hoverAndTap"
							autoPlace
							content={skill.name}
							triggerClassName="relative block shrink-0 overflow-visible leading-none"
							contentClassName="whitespace-nowrap"
						>
							<span
								className="relative block shrink-0 overflow-visible leading-none"
								style={{
									width: slotPx,
									height: targetHeightPx,
									zIndex: resolvedSkills.length - skillIndex,
								}}
							>
								{/*
								 * Absolute + translate center keeps the CSS-scaled face optically
								 * aligned with SoftBadges. Inline-flex + scale left a baseline
								 * gap that visually shifted the stack upward.
								 */}
								<span
									className="pointer-events-auto absolute left-1/2 top-1/2 inline-flex will-change-transform"
									style={{
										width: nativeFootprintPx,
										height: nativeFootprintPx,
										transform: `translate(-50%, -50%) scale(${scale})`,
									}}
								>
									<SkillBadgeTile
										skill={skill}
										{...tileProps}
										withTooltip={false}
										faceAs="span"
										className="size-full"
									/>
								</span>
							</span>
						</ContentTooltip>
					))}
				</div>
			</ContentTooltipGroup>
		);
	}

	if (count === 1) {
		return (
			<SkillBadgeTile
				skill={resolvedSkills[0]}
				{...tileProps}
				className={className}
			/>
		);
	}

	const atStart = index === 0;
	const atEnd = index === count - 1;
	const canGoPrev = scrollBehavior === "infinite" || !atStart;
	const canGoNext = scrollBehavior === "infinite" || !atEnd;
	const currentSkill = resolvedSkills[index] ?? resolvedSkills[0];
	const compactNav = size < SKILL_BADGE_DEFAULT_SIZE_PX;
	const resolvedFadeEdgesThicknessPx =
		fadeEdgesThicknessPx ?? resolveSkillBadgeCarouselFadeThicknessPx(size);

	return (
		<div
			className={cn(
				"flex items-center",
				compactNav ? "gap-1" : "gap-1.5",
				className,
			)}
			onPointerDown={(event) => event.stopPropagation()}
			onPointerEnter={shouldAutoplay ? handlePointerEnter : undefined}
			onPointerLeave={shouldAutoplay ? handlePointerLeave : undefined}
		>
			<SkillBadgeNavChevron
				direction="prev"
				disabled={!canGoPrev}
				label="Previous skill"
				compact={compactNav}
				navTone={navTone}
				onActivate={goPrev}
			/>
			<span className="sr-only" aria-live="polite">
				{currentSkill.name}, {index + 1} of {count}
			</span>
			<SkillBadgeCarouselViewport
				skill={currentSkill}
				skillIndex={index}
				slideDirection={slideDirection}
				fadeEdgesThicknessPx={resolvedFadeEdgesThicknessPx}
				{...tileProps}
			/>
			<SkillBadgeNavChevron
				direction="next"
				disabled={!canGoNext}
				label="Next skill"
				compact={compactNav}
				navTone={navTone}
				onActivate={goNext}
			/>
		</div>
	);
}
